import { useCallback, useEffect, useState } from 'react';
import { inventoryApi } from '@/api/inventory.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { useModuleScopedBreadcrumbs } from '@/hooks/useModuleScopedBreadcrumbs';
import { TablePagination } from '@/components/ui/TablePagination';
import { Skeleton } from '@/components/ui/Skeleton';
import { usePagination } from '@/hooks/usePagination';
import type { InventoryLedgerEntry } from '@/types/inventoryPlatform';
import { formatDate } from '@/utils/format';
import { showApiError } from '@/utils/apiError';

export function InventoryLedgerPage() {
  const breadcrumbs = useModuleScopedBreadcrumbs('Ledger', { label: 'Inventory', page: 'inventory' });
  const pagination = usePagination(25);
  const [rows, setRows] = useState<InventoryLedgerEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    inventoryApi
      .platformLedger({ page: pagination.page, size: pagination.size })
      .then((page) => {
        setRows(page.content);
        pagination.applyPageResponse(page);
      })
      .catch((err) => showApiError(err, 'Failed to load inventory ledger'))
      .finally(() => setLoading(false));
  }, [pagination.page, pagination.size, pagination]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <PageHeader
        title="Inventory Ledger"
        description="Immutable movement log — source of truth for all quantity changes"
        breadcrumbs={breadcrumbs}
      />
      <div className="card overflow-x-auto">
        {loading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700">
                <th className="p-3">Date</th>
                <th className="p-3">Movement</th>
                <th className="p-3">Stock ID</th>
                <th className="p-3 text-right">Delta</th>
                <th className="p-3 text-right">Before</th>
                <th className="p-3 text-right">After</th>
                <th className="p-3">Reference</th>
                <th className="p-3">Reason</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b border-slate-100 dark:border-slate-800">
                  <td className="p-3">{row.createdAt ? formatDate(row.createdAt) : '—'}</td>
                  <td className="p-3 font-mono text-xs">{row.movementType}</td>
                  <td className="p-3">{row.stockId}</td>
                  <td className="p-3 text-right">{row.quantityDelta}</td>
                  <td className="p-3 text-right">{row.quantityBefore}</td>
                  <td className="p-3 text-right font-medium">{row.quantityAfter}</td>
                  <td className="p-3 text-xs">
                    {row.referenceType ?? '—'}
                    {row.referenceId != null ? ` #${row.referenceId}` : ''}
                  </td>
                  <td className="p-3 text-xs text-slate-500">{row.reason ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && rows.length === 0 && (
          <p className="p-4 text-sm text-slate-500">
            No ledger entries yet. Post a GRN or complete a POS sale to see movements.
          </p>
        )}
        <TablePagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalElements={pagination.totalElements}
          onPageChange={pagination.setPage}
          loading={loading}
          hasRows={rows.length > 0}
        />
      </div>
    </div>
  );
}
